import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Drawer,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  Button,
  Chip,
  Fade,
  Slide,
  Divider,
  Stack,
  CircularProgress,
} from "@mui/material";
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  ShoppingCartCheckout as CheckoutIcon,
} from "@mui/icons-material";
import UserPhoto from "../../assets/images/user-icon.png";
import {
  fetchPackagesInCart,
  removeFromCart,
  setOpenPackageCartDrawer,
} from "../../redux/slices/PackageCartSlice";
import { useNavigate } from "react-router-dom";
import CustomButton from "../CustomButton";
import { FaArrowLeft } from "react-icons/fa";
import { decrementCartCount } from "../../redux/slices/userSlice";

export const PackageCartDrawer = () => {
  const {
    openPackageCartDrawer,
    loadingPackagesInCart,
    errorPackagesInCart,
    packagesInCart,
    removingFromCart,
  } = useSelector((state) => state.packageCart);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const islogin = useSelector((state) => state.user.isLogin);
  const userinfo = useSelector((state) => state.user.userInfo);
  const user = islogin ? JSON.parse(userinfo) : null;

  React.useEffect(() => {
    if (islogin && user?.UserID) {
      dispatch(fetchPackagesInCart(user.UserID));
    }
  }, [openPackageCartDrawer, islogin, user?.UserID]);

  const handleClose = () => dispatch(setOpenPackageCartDrawer(false));

  // Placeholder handlers – connect to your real redux actions
  const handleRemove = (orderId) => {
    if (!orderId) return;
    dispatch(removeFromCart(orderId));
    dispatch(decrementCartCount());
  };

  const handleCompleteOrder = (item) => {
    sessionStorage.setItem("scrollTo-Title", "true");
    navigate(`/order-summary/${item.orderId}`, {
      state: {
        cartItem: true,
        isHire: true,
      },
    });
    // dispatch(placeSingleOrderThunk(item));
    handleClose(); // optional
  };
  const firstParsedPhotoURL = (photo) => {
    if (!photo) return UserPhoto;
    try {
      const parsed = JSON.parse(photo);
      return Array.isArray(parsed) && parsed.length > 0 ? parsed[0] : UserPhoto;
    } catch (e) {
      console.error("Error parsing photo URL:", e);
      return UserPhoto;
    }
  };
  console.log(openPackageCartDrawer,"openPackageCartDrawer");
  return (
    <Drawer
      anchor="right"
      open={openPackageCartDrawer}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 380, md: 440 },
          bgcolor: "background.paper",
          borderLeft: "1px solid",
          borderColor: "divider",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <CheckoutIcon color="primary" fontSize="medium" />
          <Typography variant="h6" fontWeight={600}>
            Your Cart
          </Typography>
          <Chip
            label={packagesInCart.length}
            size="small"
            color="primary"
            sx={{ height: 20, fontSize: "0.75rem", fontWeight: 600 }}
          />
        </Box>
        <IconButton onClick={handleClose} edge="end">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflowY: "auto", p: 1 }}>
        {loadingPackagesInCart ? (
          <Box sx={{ textAlign: "center", py: 10 }}>
            <Typography color="text.secondary">Loading cart...</Typography>
          </Box>
        ) : errorPackagesInCart ? (
          <Box sx={{ textAlign: "center", py: 8, color: "error.main" }}>
            <Typography>{errorPackagesInCart}</Typography>
          </Box>
        ) : packagesInCart.length === 0 ? (
          <EmptyCartView onClose={handleClose} />
        ) : (
          <Slide direction="left" in mountOnEnter unmountOnExit timeout={350}>
            <List disablePadding>
              {packagesInCart.map((item, index) => (
                <React.Fragment key={item?.orderId}>
                  {index > 0 && <Divider sx={{ mx: 2 }} />}
                  <Fade in timeout={400 + index * 80}>
                    <ListItem
                      sx={{
                        px: 2,
                        py: 2.5,
                        flexDirection: "column",
                        alignItems: "stretch",
                        transition: "background 0.2s",
                        "&:hover": { bgcolor: "action.hover" },
                      }}
                      disableGutters
                    >
                      {/* Row 1: Avatar + Name + Price + Delete */}
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={2}
                        sx={{ width: "100%", mb: 1.5 }}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={2}
                          sx={{ flex: 1 }}
                        >
                          <Avatar
                            variant="rounded"
                            src={firstParsedPhotoURL(item?.Photo || UserPhoto)}
                            sx={{
                              width: 56,
                              height: 56,
                              borderRadius: 2,
                              bgcolor: "grey.200",
                            }}
                          />

                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="subtitle1"
                              fontWeight={600}
                              // noWrap
                              sx={{ lineHeight: 1.3 }}
                            >
                              {item?.serviceName}
                            </Typography>
                            <Typography
                              variant="h6"
                              color="primary"
                              fontWeight={700}
                              component="div"
                              sx={{ mt: 0.25 }}
                            >
                              $
                              {Number(item?.totalAmount ?? 0).toLocaleString(
                                "en-US",
                              )}
                            </Typography>
                          </Box>
                        </Stack>

                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemove(item?.orderId)}
                          sx={{
                            "&:hover": { bgcolor: "error.lighter" },
                          }}
                        >
                          {removingFromCart ? (
                            <CircularProgress size="small" />
                          ) : (
                            <DeleteIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Stack>
                      <CustomButton
                        onClick={() => handleCompleteOrder(item)}
                        type="fill_default"
                        icon={CheckoutIcon}
                        title="Checkout"
                        className="w-100"
                      >
                        Checkout
                      </CustomButton>
                    </ListItem>
                  </Fade>
                </React.Fragment>
              ))}
            </List>
          </Slide>
        )}
      </Box>

      {/* Footer - global checkout */}
      {packagesInCart.length > 0 && (
        <Box
          sx={{
            p: 3,
            borderTop: 1,
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <Button
            variant="contained"
            fullWidth
            size="large"
            startIcon={<CheckoutIcon />}
            sx={{
              py: 1.5,
              borderRadius: 2,
              fontSize: "1rem",
              fontWeight: 600,
              boxShadow: 3,
              "&:hover": { boxShadow: 6, transform: "translateY(-1px)" },
              transition: "all 0.2s ease",
            }}
            onClick={() => {
              navigate("/dashboardpage/orders");
              handleClose();
            }}
          >
            View All Orders
          </Button>

          <Typography
            variant="caption"
            color="text.secondary"
            align="center"
            display="block"
            mt={2}
          >
            Prices include all taxes
            {/* Prices include all taxes • Packages are non-transferable */}
          </Typography>
        </Box>
      )}
    </Drawer>
  );
};

function EmptyCartView({ onClose }) {
  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        p: 4,
        color: "text.secondary",
      }}
    >
      <CheckoutIcon sx={{ fontSize: 72, color: "grey.300", mb: 3 }} />
      <Typography variant="h6" gutterBottom color="text.primary">
        Your cart is empty
      </Typography>
      <Typography variant="body1" sx={{ mb: 4, maxWidth: 260 }}>
        Add services or packages to get started!
      </Typography>
      {/* <Button variant="outlined" color="primary" onClick={onClose}>
        Continue Browsing
      </Button> */}
      <CustomButton
        onClick={onClose}
        type="fill_default"
        icon={FaArrowLeft}
        title="Continue Browsing"
        className="w-100"
      >
        Continue Browsing
      </CustomButton>
    </Box>
  );
}
